import { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDirectorMakeups,
  useDirectorCompletedMakeups,
  useDirectorScheduleMakeup,
  useDirectorUpdateMakeupStatus,
  useDirectorSessionsForDate,
} from '@/hooks/use-makeups';
import { MakeupsTable } from '@/components/director/makeups/makeups-table';
import { MakeupFormModal } from '@/components/director/makeups/makeup-form-modal';

export default function DirectorMakeupsPage() {
  const { data, isLoading } = useDirectorMakeups();
  const { data: completedMakeups } = useDirectorCompletedMakeups();
  const scheduleMakeup = useDirectorScheduleMakeup();
  const updateStatus = useDirectorUpdateMakeupStatus();
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedMakeupId, setSelectedMakeupId] = useState<string | null>(null);
  const [sessionDate, setSessionDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  });
  const { data: sessionsForDate } = useDirectorSessionsForDate(sessionDate);

  const pendingMakeups = data?.makeups.filter(m => m.status === 'pending') ?? [];
  const scheduledMakeups = data?.makeups.filter(m => m.status === 'scheduled') ?? [];

  const handleSchedule = async (makeupSessionId: string) => {
    if (!selectedMakeupId) return;
    await scheduleMakeup.mutateAsync({ makeupId: selectedMakeupId, makeupSessionId });
    setScheduleModalOpen(false);
    setSelectedMakeupId(null);
  };

  const handleComplete = async (makeupId: string) => {
    await updateStatus.mutateAsync({ makeupId, status: 'completed' });
  };

  const handleWaive = async (makeupId: string) => {
    await updateStatus.mutateAsync({ makeupId, status: 'waived', notes: 'Waived by director' });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <CalendarClock className="w-6 h-6 text-orange-500" />
          Makeup Sessions
        </h1>
        <p className="text-muted-foreground">Organization-wide makeup session management</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-orange-500">{pendingMakeups.length}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-blue-500">{scheduledMakeups.length}</p>
            <p className="text-xs text-muted-foreground">Scheduled</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-green-500">{completedMakeups?.length ?? 0}</p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
      </div>

      <MakeupsTable
        title="Pending Makeups"
        description="These players need a makeup session scheduled"
        makeups={pendingMakeups}
        variant="pending"
        onSchedule={(makeupId) => {
          setSelectedMakeupId(makeupId);
          setScheduleModalOpen(true);
        }}
        onWaive={handleWaive}
      />

      <MakeupsTable
        title="Scheduled Makeups"
        description="These makeups have a session assigned"
        makeups={scheduledMakeups}
        variant="scheduled"
        onComplete={handleComplete}
        onWaive={handleWaive}
      />

      {completedMakeups && (
        <MakeupsTable
          title="History"
          description=""
          makeups={completedMakeups}
          variant="history"
        />
      )}

      {/* Empty state */}
      {data?.makeups.length === 0 && (!completedMakeups || completedMakeups.length === 0) && (
        <Card>
          <CardContent className="p-8 text-center">
            <CalendarClock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No makeup sessions</p>
            <p className="text-sm text-gray-400 mt-1">
              Makeup sessions are created when a player is marked absent and flagged for a makeup.
            </p>
          </CardContent>
        </Card>
      )}

      <MakeupFormModal
        open={scheduleModalOpen}
        onOpenChange={setScheduleModalOpen}
        sessionDate={sessionDate}
        onSessionDateChange={setSessionDate}
        sessions={sessionsForDate}
        onSelectSession={handleSchedule}
        isPending={scheduleMakeup.isPending}
      />
    </div>
  );
}
